import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Button, Container, Row, Col, Modal, ProgressBar } from "react-bootstrap";
import { Switch, Route, useHistory, useRouteMatch, useLocation, useParams } from 'react-router-dom';
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { userActions, resourceActions, templateActions } from '../../actions';
import { useTranslation } from 'react-i18next';
import PageTitle from "../../components/common/PageTitle";
import AuthImage from "../../components/common/AuthImage";
import { useFormik } from 'formik';
import { educationTaxonomyService } from '../../services';

const selectedOptionValues = event => Array.from(event.target.options)
    .filter(option => option.selected)
    .map(option => option.value);

const AddResource = params => {
    const { lite, types, uploadBtn, tempId, education } = params;
    const history = useHistory();
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // const user = useSelector(state => state.authentication.user);
    const loading = useSelector(state => state.template.status);
    const uploading = useSelector(state => state.resource.uploading);
    const uploadingIndex = useSelector(state => state.resource.multiUploadIndex);
    const uploadingTotal = useSelector(state => state.resource.multiUploadTotal);
    const uploadProgress = useSelector(state => state.resource.uploadProgress);
    const uploadResult = useSelector(state => state.resource.uploadResult);
    const uploadError = useSelector(state => state.resource.error);
    const templateId = useSelector(state => state.template.templateId);
    const group = useSelector(state => state.authentication.user.group);
    const [sendUpdate, setSendUpdate] = useState(0)
    const [multiUpload, setMultiUpload] = useState({ status: '', current: 0, total: 0 })
    const [resourceType, setResourceType] = useState('');
    const [resourceFileType, setResourceFileType] = useState('');
    const [selectedFile, setSelectedFile] = useState('');
    const [createComplete, setCreateComplete] = useState();
    const [uploadFailures, setUploadFailures] = useState([]);
    const uploadSessionRef = useRef({ active: false, failed: false, lastIndex: -1 });
    const [frameName, setFrameName] = useState();
    const [taxonomy, setTaxonomy] = useState([]);
    const [educationScope, setEducationScope] = useState('educational');
    const [educationGradeCodes, setEducationGradeCodes] = useState([]);
    const [educationSubjectCodes, setEducationSubjectCodes] = useState([]);
    const [educationTopicCodes, setEducationTopicCodes] = useState([]);
    const fileInputRef = useRef();
    const uploadFormRef = useRef();
    const tagsFaRef = useRef();
    const uploadBtnRef = useRef();
    const frameNameRef = useRef();
    const maskPathRef = useRef();
    const frameWidthRef = useRef();
    const frameHeightRef = useRef();
    let nameTimeout = null;

    useEffect(() => {
        if (lite) return;
        educationTaxonomyService.findAll({ active: true })
            .then(result => setTaxonomy(Array.isArray(result) ? result : []))
            .catch(() => setTaxonomy([]));
    }, [lite]);

    const educationGrades = useMemo(() => taxonomy.filter(item => item.type === 'grade' && item.active), [taxonomy]);
    const educationSubjects = useMemo(() => taxonomy.filter(item => item.type === 'subject' && item.active
        && (!(item.gradeCodes || []).length || !educationGradeCodes.length
            || item.gradeCodes.some(code => educationGradeCodes.includes(code)))), [taxonomy, educationGradeCodes]);
    const educationTopics = useMemo(() => taxonomy.filter(item => item.type === 'topic' && item.active
        && (!educationSubjectCodes.length || educationSubjectCodes.includes(item.subjectCode))
        && (!(item.gradeCodes || []).length || !educationGradeCodes.length
            || item.gradeCodes.some(code => educationGradeCodes.includes(code)))), [taxonomy, educationGradeCodes, educationSubjectCodes]);

    useEffect(() => {
        setEducationSubjectCodes(current => {
            const nextCodes = current.filter(code => educationSubjects.some(item => item.code === code));
            return _.isEqual(nextCodes, current) ? current : nextCodes;
        });
        setEducationTopicCodes(current => {
            const nextCodes = current.filter(code => educationTopics.some(item => item.code === code));
            return _.isEqual(nextCodes, current) ? current : nextCodes;
        });
    }, [educationSubjects, educationTopics]);

    const resourcesTypes = [
        { name: 'Image', accept: 'image/jpeg,image/png,image/svg+xml,image/gif' },
        { name: 'Shape', accept: 'image/svg+xml' },
        { name: 'Vector', accept: 'image/svg+xml' },
        { name: 'Video', accept: 'video/mp4,video/quicktime' },
        { name: 'Frame', accept: 'image/svg+xml', disable: true },
        { name: 'Grid', disable: true },
        { name: 'Line', disable: true },
    ]

    const resourceTypes = [
        { name: 'Image/Vector/Video', type: 'file', accept: 'image/jpeg,image/png,image/svg+xml,image/gif,image/svg+xml,video/mp4,video/quicktime' },
        { name: 'Frame', type: 'frame', accept: 'image/svg+xml', disable: false },
        // { name: 'Grid', type: 'grid', disable: true },
        // { name: 'Line', type: 'line', disable: true },
    ]

    const uploadTypes = ['Image', 'Shape', 'Vector', 'Video'];

    useEffect(() => {
        if (uploadBtn) {
            const btnHolder = document.getElementById(uploadBtn)
            btnHolder && btnHolder.append(uploadBtnRef.current)
            uploadBtnRef.current.style.opacity = 1;
        }
    }, [uploadBtn, uploadBtnRef])

    useEffect(() => {
        const session = uploadSessionRef.current;
        if (session.active && (uploadResult === 'success' || uploadResult === 'failed')
            && uploadingIndex > session.lastIndex && uploadingIndex + 1 <= uploadingTotal) {
            session.lastIndex = uploadingIndex;
            const preview = document.getElementById(`preview_${uploadingIndex}`)
            preview && preview.classList.remove('showLoader')
            if (uploadResult === 'success') {
                preview && preview.classList.add('success')
            } else {
                preview && preview.classList.add('danger')
                session.failed = true;
                const filename = Array.isArray(selectedFile) ? selectedFile[uploadingIndex].name : selectedFile.name;
                const message = _.get(uploadError, 'error') || _.get(uploadError, 'message') || 'Upload failed. Please try again.';
                setUploadFailures(current => [...current, `${filename}: ${message}`]);
            }
            if (uploadingIndex + 1 < (uploadingTotal)) {
                uploadFormHandleMulti(uploadingIndex + 1)
            } else {
                session.active = false;
                setCreateComplete(session.failed ? -1 : 2);
            }
        }
    }, [uploadResult, uploadError, uploadingIndex, uploadingTotal, selectedFile])

    const beginUpload = () => {
        uploadSessionRef.current = { active: true, failed: false, lastIndex: -1 };
        setUploadFailures([]);
        setCreateComplete(1);
    };

    const resetForm = () => {
        uploadFormRef.current.reset();
        setResourceType('');
        setResourceFileType('');
        setSelectedFile('');
        setFrameName('');
        setCreateComplete(undefined);
        setUploadFailures([]);
    }

    useEffect(() => {
        // checkFrameName()
        if (frameName) {
            maskPathHandler()
        }
    }, [frameName])

    const svgMask = (element) => {
        const path = _.get(element, 'path', element)
        const text = (`
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${frameWidthRef.current.value} ${frameHeightRef.current.value}">
                <mask id="mask" fill="#fff">
                    ${path}
                </mask>
                <g id="preview" fill="#5f5">
                    ${path}
                    <image href="/assets/images/noPic.jpg" height="100%" width="100%" preserveAspectRatio="xMidYMid slice" mask="url(#mask)" />
                </g>
            </svg>
        `)
        var node = document.createElement('svg')
        var parser = new DOMParser();
        var doc = parser.parseFromString(text, "image/svg+xml");
        // node.appendChild(document.createTextNode(text));
        node.innerHTML = text;
        // first an doctype
        var svgDocType = document.implementation.createDocumentType('svg', "-//W3C//DTD SVG 1.1//EN", "http://www.w3.org/Graphics/SVG/1.1/DTD/svg11.dtd");
        // then a new SVG Document
        var svgDoc = document.implementation.createDocument('http://www.w3.org/2000/svg', 'svg', svgDocType);
        // set its documentElement to our root svg node (well a clone of it)
        svgDoc.replaceChild(node.cloneNode(true), svgDoc.documentElement);
        // serialize the document
        var svgData = (new XMLSerializer()).serializeToString(doc);
        setResourceFileType('Frame')
        return svgData;
    }

    const uploadFormHandle = () => {
        if (uploadSessionRef.current.active || !selectedFile.file) return;
        uploadFormRef.current.reportValidity();
        if (!uploadFormRef.current.checkValidity()) return;
        if (_.has(tagsFaRef, 'current.value')) {
            tagsFaRef.current.value = tagsFaRef.current.value.replace(/،/g, ",")
        }
        const formData = new FormData(uploadFormRef.current);
        formData.set('files', selectedFile.file);

        const fileType = selectedFile.file.type.split('/')[1];
        const uploadParams = {
            type: fileType === 'gif' ? 'Vector' : (resourceFileType || selectedFile.type),
        };
        beginUpload();
        dispatch(resourceActions.upload(formData, uploadParams));
    }

    const uploadFormHandleMulti = (uploadIndex) => {
        if (uploadIndex === 0 && uploadSessionRef.current.active) return;
        if (!Array.isArray(selectedFile) || selectedFile.length === 0) return;
        if (!uploadFormRef.current.reportValidity()) return;
        if (!selectedFile.every((file) => file.type === selectedFile[0].type)) {
            alert('All file must have same type.')
            return;
        }
        if (uploadIndex === 0) {
            uploadFormRef.current.querySelectorAll('.multiResourcePreview').forEach(target => {
                target.classList.remove('showLoader', 'success', 'danger');
            });
        }
        const preview = document.getElementById(`preview_${uploadIndex}`);
        if (preview) preview.classList.add('showLoader');
        if (_.has(tagsFaRef, 'current.value')) {
            tagsFaRef.current.value = tagsFaRef.current.value.replace(/،/g, ",")
        }
        const formData = new FormData(uploadFormRef.current);
        formData.delete("files");
        formData.append("files", selectedFile[uploadIndex].file);

        const fileType = selectedFile[uploadIndex].file.type.split('/')[1];
        const uploadParams = {
            fileIndex: uploadIndex,
            total: selectedFile.length,
            type: fileType === 'gif' ? 'Vector' : (resourceFileType || selectedFile[uploadIndex].type),
        };
        if (uploadIndex === 0) beginUpload();
        dispatch(resourceActions.upload(formData, uploadParams));
    }

    const resourceTypeChangeHandler = (e) => {
        setResourceType(e.currentTarget.value)
        setResourceFileType('')
        setSelectedFile('')
        setFrameName('')
    }

    function svgToBlob(svgData) {
        var blob = new Blob([svgData], {
            type: 'image/svg+xml',
            name: 'test.svg'
        });
        return blob;
    }

    function createFileList(a) {
        a = [].slice.call(Array.isArray(a) ? a : arguments)
        for (var c, b = c = a.length, d = !0; b-- && d;) d = a[b] instanceof File
        if (!d) throw new TypeError('expected argument to FileList is File or array of File objects')
        for (b = (new ClipboardEvent('')).clipboardData || new DataTransfer; c--;) b.items.add(a[c])
        return b.files
    }

    const maskPathHandler = () => {
        const blob = svgToBlob(svgMask(_.get(maskPathRef, 'current.value', '')));
        var maskFile = new File([blob], `${frameName}.svg`, { type: 'image/svg+xml' });
        const files = createFileList(maskFile);
        fileInputRef.current.files = files;
        setTimeout(() => { fileChange({ target: { files: files } }) });
    }

    const fileChange = e => {
        if (resourceType !== 'frame') setResourceFileType('');
        const _URL = window.URL || window.webkitURL;
        const reader = new FileReader();
        if (e.target.files.length == 1) {
            const file = e.target.files[0];
            let previewSrc;

            if (file.type.includes('image') || file.type.includes('video')) {
                const img = new Image();
                var objectUrl = _URL.createObjectURL(file);
                img.onload = function () {
                    _URL.revokeObjectURL(objectUrl);
                    if (file.type.includes('svg')) {
                        reader.readAsText(file);
                    }
                };
                previewSrc = objectUrl;
            }

            setSelectedFile({
                file: e.target.files[0],
                name: file.name,
                fileType: file.type.split('/')[0],
                imageType: file.type.split('/')[1],
                type: file.type.split('/')[1].includes('svg') ? 'Vector' : _.capitalize(file.type.split('/')[0]),
                preview: previewSrc,
            })
        } else {
            let files = [];
            [...e.target.files].map((file, index) => {
                let previewSrc;
                if (file.type.includes('image') || file.type.includes('video')) {
                    const img = new Image();
                    var objectUrl = _URL.createObjectURL(file);
                    img.onload = function () {
                        _URL.revokeObjectURL(objectUrl);
                        if (file.type.includes('svg')) {
                            reader.readAsText(file);
                        }
                    };
                    previewSrc = objectUrl;
                }
                files.push(
                    {
                        file: e.target.files[index],
                        name: file.name,
                        fileType: file.type.split('/')[0],
                        imageType: file.type.split('/')[1],
                        type: file.type.split('/')[1].includes('svg') ? 'Vector' : _.capitalize(file.type.split('/')[0]),
                        preview: previewSrc,
                    }
                )
            });
            setSelectedFile(files);
        }
    }

    const svgTypeHandler = e => {
        setSelectedFile({ ...selectedFile, type: e.currentTarget.value })
    }

    const loadPreview = (event) => {
        document.getElementById('name').value = selectedFile.name;
        // if (event.target.tagName === 'VIDEO') {
        //   document.getElementById('width').value = event.target.videoWidth;
        //   document.getElementById('height').value = event.target.videoHeight;
        //   document.getElementById('duration').value = event.target.duration;
        // } else {
        //   document.getElementById('width').value = event.target.width;
        //   document.getElementById('height').value = event.target.height;
        // }
    }

    const frameNameHandler = (e) => {
        const name = e.target.value;
        if (nameTimeout) {
            clearTimeout(nameTimeout);
        }
        nameTimeout = setTimeout(() => { setFrameName(name) }, 700);
    }

    const tags = (
        <Row>
            <Col>
                <span>Tags (En):</span>
                <textarea id="tags" name="tags" />
            </Col>
            <Col>
                <span>Tags (Fa):</span>
                <textarea id="tagsFa" ref={tagsFaRef} name="tagsFa" />
            </Col>
        </Row>);

    const userGroup = lite ? <input type="hidden" name="group" value={group || ''} readOnly /> : (
        <Col>
            <span>Group:</span>
            <select id="group" name="group" defaultValue="" >
                <option value="">All Groups</option>
                <option value="vibino">Monda</option>
                <option value="bdok">bdok</option>
            </select>
        </Col>
    )

    const resourceValue = (
        <Col style={lite && { display: 'none' }}>
            <span>Value:</span>
            <select id="price" name="price"  >
                <option value="0">Free</option>
                <option value="1">Premium</option>
            </select>
        </Col>
    )

    const frameDim = (
        <>
            <Col>
                <span>Width:</span>
                <input ref={frameWidthRef} id="frameWidth" name="frameWidth" type="number" required onChange={maskPathHandler} />
            </Col>
            <Col>
                <span>Height:</span>
                <input ref={frameHeightRef} id="frameHeight" name="frameHeight" type="number" required onChange={maskPathHandler} />
            </Col>
        </>
    )

    const imageType = (
        <>
            {(_.get(selectedFile, '[0]fileType', '') === 'image' || _.get(selectedFile, 'fileType', '') === 'image') && !_.get(selectedFile, 'imageType', '').includes('svg') &&
                <Col style={lite && { display: 'none' }}>
                    <span>Type:</span>
                    <select id="imageType" name="imageType" value={resourceFileType || 'Image'} onChange={(e) => setResourceFileType(e.target.value)}>
                        <option value="Image">Photo</option>
                        <option value="Background">Background</option>
                        <option value="Vector">Sticker</option>
                    </select>
                </Col>
            }
        </>
    )

    const resourceStatus = lite ? <input type="hidden" name="invisible" value="true" readOnly /> : (
        <Col>
            <span>Status:</span>
            <select id="invisible" name="invisible" defaultValue="false">
                <option value={false}>Published</option>
                <option value={true}>Unpublished</option>
            </select>
        </Col>
    )

    const maskPath = (
        <Row>
            <Col>
                <span>Mask Path:</span><span style={{ fontStyle: 'italic', marginTop: '-10px', fontSize: '92%' }}>(Please insert single svg basic shape element like 'circle', 'path', 'polygon' and 'rect')</span>
                <textarea required ref={maskPathRef} onChange={maskPathHandler} style={{ minHeight: '4rem' }} />
            </Col>
        </Row>
    )

    const maintainRatio = (
        <>
            {_.get(selectedFile, 'imageType', '').includes('svg') &&
                <Col>
                    <span>Maintain ratio:</span>
                    <select id="vectorType" name="vectorType" value={selectedFile.type || 'Vector'} onChange={svgTypeHandler} >
                        <option value="Vector">Yes</option>
                        <option value="Shape">No</option>
                    </select>
                </Col>
            }
        </>
    )

    const resourceName = (
        <Col >
            <span>Name:</span>
            {resourceType === 'frame' && <input required onChange={frameNameHandler} id="frameName" name="frameName" />}
            <input id="name" name="name" hidden={resourceType === 'frame'} disabled />
        </Col>
    )

    const vectorEditable = (
        <>
            {_.get(selectedFile, 'imageType', '').includes('svg') &&
                <Col>
                    <span>Editable:</span>
                    <select id="editable" name="editable"  >
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                    </select>
                </Col>
            }
        </>
    )

    const educationMetadataFields = !lite && (
        <Row>
            <Col>
                <span>Education scope:</span>
                <select value={educationScope} onChange={event => setEducationScope(event.target.value)}>
                    <option value="educational">Educational library</option>
                    <option value="core">Core editor tool (VLine/VShape)</option>
                    <option value="legacy">Legacy / hidden library</option>
                </select>
                <input type="hidden" name="educationScope" value={educationScope} readOnly />
            </Col>
            <Col>
                <span>Grades:</span>
                <select
                    multiple
                    disabled={educationScope !== 'educational'}
                    value={educationGradeCodes}
                    onChange={event => setEducationGradeCodes(selectedOptionValues(event))}
                    style={{ minHeight: 105 }}
                >
                    {educationGrades.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
                </select>
                <input type="hidden" name="gradeCodes" value={educationScope === 'educational' ? educationGradeCodes.join(',') : ''} readOnly />
            </Col>
            <Col>
                <span>Subjects:</span>
                <select
                    multiple
                    disabled={educationScope !== 'educational'}
                    value={educationSubjectCodes}
                    onChange={event => setEducationSubjectCodes(selectedOptionValues(event))}
                    style={{ minHeight: 105 }}
                >
                    {educationSubjects.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
                </select>
                <input type="hidden" name="subjectCodes" value={educationScope === 'educational' ? educationSubjectCodes.join(',') : ''} readOnly />
            </Col>
            <Col>
                <span>Topics:</span>
                <select
                    multiple
                    disabled={educationScope !== 'educational'}
                    value={educationTopicCodes}
                    onChange={event => setEducationTopicCodes(selectedOptionValues(event))}
                    style={{ minHeight: 105 }}
                >
                    {educationTopics.map(item => <option key={item.code} value={item.code}>{item.title}</option>)}
                </select>
                <input type="hidden" name="topicCodes" value={educationScope === 'educational' ? educationTopicCodes.join(',') : ''} readOnly />
            </Col>
        </Row>
    );

    return (
        <>
            <form id="uploadForm" ref={uploadFormRef} className={sendUpdate !== 0 && loading === 'loading' ? 'blur' : ""} >{/* onSubmit={uploadFormHandle} > */}
                <Row>
                    <Col>
                        <Row>
                            <Col lg="3">
                                <span>Resource Type:</span>
                                <select id="resourceType" name="resourceType" onChange={resourceTypeChangeHandler} >
                                    <option value="" hidden>---</option>
                                    {resourceTypes.map(option =>
                                        <option value={option.type} key={option.type}>{`${option.name}`}</option>
                                    )}
                                </select>
                            </Col>
                            <input
                                ref={fileInputRef}
                                type="file"
                                name="files"
                                accept="image/jpeg,image/png,image/svg+xml,video/mp4,video/quicktime,image/gif"
                                onChange={fileChange}
                                hidden
                                multiple
                            />
                            {tempId && <input hidden name="tempId" value={tempId} readOnly />}
                            {lite && <input hidden name="educationScope" value="educational" readOnly />}
                            {lite && <input hidden name="gradeCodes" value={_.get(education, 'gradeCode', '')} readOnly />}
                            {lite && <input hidden name="subjectCodes" value={_.get(education, 'subjectCode', '')} readOnly />}
                            {lite && <input hidden name="topicCodes" value={(_.get(education, 'topicCodes', []) || []).join(',')} readOnly />}
                            {resourceType === 'file' &&
                                <Col>
                                    <Button
                                        style={{ position: 'absolute', bottom: 0, fontSize: '100%', minWidth: '150px' }}
                                        className="vButton colorPrimary "
                                        // content={t('uploadPictureVideo')}
                                        onClick={() => fileInputRef.current.click()}
                                    >{!selectedFile.name ? 'Add File...' : 'Change File...'}</Button>
                                </Col>
                            }
                        </Row>
                        {!resourceType &&
                            (<Row>
                                <Col>
                                    <h5>Please choose resource type.</h5>
                                </Col>
                            </Row>)
                        }
                        {(resourceType === 'frame' || (resourceType === 'file' && selectedFile.name)) &&
                            (<Row>
                                {_.get(selectedFile, 'fileType') === 'video' ?
                                    <Col lg="4" className="resourcePreview"><video onLoadedData={loadPreview} src={selectedFile.preview} className="editTemplatePreview" controls /></Col>
                                    :
                                    <Col lg="4" className="resourcePreview"><img style={resourceType === 'frame' ? { boxShadow: '0px 0px 12px #ABABAB' } : {}} onLoad={loadPreview} src={selectedFile.preview} className="editTemplatePreview" /></Col>
                                }
                                <Col>
                                    <Row>
                                        {resourceName}
                                        {resourceType !== 'frame' && imageType}
                                        {userGroup}
                                        {resourceValue}
                                        {resourceStatus}
                                    </Row>
                                    <Row>
                                        {resourceType === 'frame' && frameDim}
                                        {resourceType !== 'frame' && vectorEditable}
                                        {resourceType !== 'frame' && maintainRatio}
                                    </Row>
                                    {resourceType === 'frame' && maskPath}
                                    {!lite && tags}
                                    {educationMetadataFields}
                                </Col>
                            </Row>
                            )
                        }
                        {(resourceType === 'file' && Array.isArray(selectedFile)) &&
                            (<>
                                <Row className="multiResourceHolder">
                                    {selectedFile.map((file, index) => {
                                        if (_.get(file, 'fileType') === 'video') {
                                            return <span key={`${file.name}-${index}`} id={`preview_${index}`} className="multiResourcePreview"><video onLoadedData={loadPreview} src={file.preview} className="editTemplatePreview" controls /></span>
                                        } else {
                                            return <span key={`${file.name}-${index}`} id={`preview_${index}`} className="multiResourcePreview"><div className="loaderHolder"><div className="loader"></div></div><img style={resourceType === 'frame' ? { boxShadow: '0px 0px 12px #ABABAB' } : {}} onLoad={loadPreview} src={file.preview} className="editTemplatePreview" /></span>
                                        }

                                        // return (<span className="resourcePreview"><img onLoad={loadPreview} src={file.preview} className="editTemplatePreview" /></span>)
                                    }
                                    )}
                                </Row>
                                <Row>
                                    <Col>
                                        <Row>
                                            {resourceName}
                                            {resourceType !== 'frame' && imageType}
                                            {userGroup}
                                            {resourceValue}
                                            {resourceStatus}
                                        </Row>
                                        <Row>
                                            {resourceType === 'frame' && frameDim}
                                            {resourceType !== 'frame' && vectorEditable}
                                            {resourceType !== 'frame' && maintainRatio}
                                        </Row>
                                        {resourceType === 'frame' && maskPath}
                                        {!lite && tags}
                                        {educationMetadataFields}
                                    </Col>
                                </Row>
                            </>
                            )
                        }
                    </Col>
                </Row>

            </form>
            {uploadFailures.length > 0 && (
                <div role="alert" className="alert alert-danger">
                    <strong>{createComplete === -1 ? 'Some files could not be uploaded.' : 'Upload error'}</strong>
                    {uploadFailures.map((message, index) => <div key={index}>{message}</div>)}
                    {createComplete === -1 && <div>Successful files are already saved. Select only the failed files to retry.</div>}
                </div>
            )}
            {Array.isArray(selectedFile) ?
                <Button ref={uploadBtnRef} style={{ opacity: '0' }} type="submit" variant="success" disabled={uploading || createComplete === 1} onClick={() => uploadFormHandleMulti(0)}><i className="material-icons"></i> Upload Queue</Button>
                :
                <Button ref={uploadBtnRef} style={{ opacity: '0' }} type="submit" variant="success" onClick={uploadFormHandle} disabled={uploading || createComplete === 1 || !_.has(selectedFile, 'name')}><i className="material-icons"></i> Upload</Button>
            }
            {createComplete === 2 && (
                <div className="vModal">
                    <div className="vModalContent">
                        <div className="vModalHeader">Upload complete</div>
                        <div className="vModalBody">{uploadingTotal} resource{uploadingTotal === 1 ? '' : 's'} uploaded successfully.</div>
                        <div className="vModalFooter">
                            <Button theme="success" onClick={() => history.push(`/templates-list`)}><i className="material-icons"></i> Start Design</Button>
                            {params.templateId &&
                                <Button className="" theme="primary" onClick={() => {history.push(`/template/${params.templateId}/edit`);window.location.reload();}}><i className="material-icons"></i> Upload Resources</Button>
                            }
                            <Button className="" theme="primary" onClick={resetForm}><i className="material-icons"></i> Upload more</Button>
                            <Button className="" theme="danger" onClick={() => history.push(params.templateId ? '/templates-list' : '/resources-list')}><i className="material-icons"></i> Close</Button>
                        </div>
                    </div>
                </div>
            )}
            <Modal
                size="sm"
                show={uploading}
                // onHide={() => setSmShow(false)}
                aria-labelledby="example-modal-sizes-title-sm"
                // centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body>
                    Uploading...
                    <ProgressBar animated now={!Array.isArray(selectedFile) ? uploadProgress : 100 / uploadingTotal * uploadingIndex} />
                </Modal.Body>
            </Modal>
        </>

    );
}

export default AddResource;
